import { AirToPremiereEvent } from '@air/premiere-constants';

import { sendPluginHostMessage } from '~/lib/pluginMessages';

/**
 * Allowed protocol schemes. Defense-in-depth against an XSS-compromised
 * WebView passing `file://` or custom scheme handlers (`slack://`,
 * `vscode://`, etc.) into the OpenUrl bridge, which would otherwise hand
 * them to `shell.openExternal` on the host. Host validates the same
 * allowlist in `plugins/premiere/src/events/openUrl.ts` for symmetry.
 */
const ALLOWED_PROTOCOLS = new Set(['https:', 'http:']);

const isAllowedUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url);

    return ALLOWED_PROTOCOLS.has(parsed.protocol);
  } catch {
    return false;
  }
};

/**
 * Redirects the current window. Used during logout where we need
 * a hard navigation rather than opening a new tab.
 */
export const windowUrlOpener = (url: string) => {
  window.location.href = url;
};

/**
 * Opens the URL in the system browser via the UXP host (shell.openExternal).
 * Used for OAuth sign-in — UXP WebView cannot navigate to external auth
 * domains (Google, Apple) that aren't in the webview allowlist, and
 * window.open() is silently no-op in the UXP WebView context.
 */
export const uxpUrlOpener = (url: string) => {
  if (!isAllowedUrl(url)) {
    console.error('[Air Premiere] uxpUrlOpener rejected: only http(s) URLs are allowed, got', url);
    return;
  }

  if (window.uxpHost) {
    sendPluginHostMessage({ data: { url }, type: AirToPremiereEvent.OpenUrl });
  } else {
    // Browser dev mode — window.open works normally outside UXP
    window.open(url, '_blank');
  }
};
